import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'react-tooltip';

import { TaxConfiguration } from '@cashier/cashier-model';
import { cn } from '@cashier/cashier-ui';

import { useTaxInformation } from './useTaxInformation';

import styles from './styles.module.scss';

interface TaxInformationProps {
  type: 'oldCashier' | 'newCashier';
  configuration: TaxConfiguration;
}

export const TaxInformation = ({
  type,
  configuration,
}: TaxInformationProps): ReactElement => {
  const { t } = useTranslation();
  const { taxAmount, currencySymbol } = useTaxInformation(configuration);

  const { taxPercentage, deductibleStake } = configuration;
  const amount = ''; //TODO: temporary code due to new payload structure

  const tooltipStyles = {
    backgroundColor:
      type === 'newCashier' ? '#768C8F' : 'rgba(255, 255, 255, 1)',
    padding: '8px',
    width: '230px',
    color:
      type === 'newCashier'
        ? 'rgba(180, 198, 199, 1)'
        : 'rgba(118, 140, 143, 1)',
    borderRadius: '8px',
  };

  return (
    <div className={cn([styles.container, styles[type]])}>
      <span className={cn([styles.title, styles[type]])}>
        {t('taxInformation.heading')}
      </span>
      <span className={cn([styles.description, styles[type]])}>
        {t('taxInformation.description', {
          percent: Number.parseFloat(taxPercentage),
        })}
      </span>
      <div className={cn([styles.taxBlock, styles[type]])}>
        <div className={styles.flexContainer}>
          <div className={styles.leftSide}>
            {t('taxInformation.payoutLabel')}
          </div>
          <div className={styles.rightSide}>
            {currencySymbol} {amount || 0}
          </div>
        </div>
        <div className={styles.flexContainer}>
          <div className={styles.leftSide}>
            {t('taxInformation.lessWithholdingTax')} (
            {Number.parseFloat(taxPercentage)}%&nbsp;
            <span className={cn([styles.link, styles[type]])} id="taxHint">
              {t('taxInformation.grossWinnings')}
            </span>
            )
          </div>
          <div className={styles.rightSide}>
            <div className={styles.rightSide}>
              {currencySymbol} {taxAmount || 0}
            </div>
          </div>
        </div>
        <div className={styles.delimiter} />
        <div className={styles.flexContainer}>
          <div className={styles.leftSide}>
            {t('taxInformation.netPayoutLabel')}
          </div>
          {currencySymbol} {(Number(amount) - Number(taxAmount)).toFixed(2)}
        </div>
      </div>
      <Tooltip
        anchorSelect="#taxHint"
        openOnClick
        place="bottom"
        noArrow
        opacity={1}
        style={{ ...tooltipStyles }}
      >
        <div className={styles.flexTooltipContainer}>
          <div>{t('taxInformation.payoutLabel')}</div>
          <div>
            {currencySymbol} {amount || 0}
          </div>
        </div>
        <div className={styles.flexTooltipContainer}>
          <div>{t('taxInformation.lessDeductibleStake')}</div>
          <div>
            {currencySymbol} {Number.parseFloat(deductibleStake)}
          </div>
        </div>
        <div className={styles.delimiter} />
        <div className={cn([styles.flexTooltipContainer, styles[type]])}>
          <div>{t('taxInformation.grossWinnings')}</div>
          <div>
            {currencySymbol} {taxAmount || 0}
          </div>
        </div>
      </Tooltip>
    </div>
  );
};
